import {
  getSession,
  useHandleAuthFlowForAction,
  useUserId,
} from '@dreamstack/auth'
import { useTranslation } from '@dreamstack/i18n'
import { Heart } from '@dreamstack/icons/src/icon-components/Heart'
import { HeartLiked } from '@dreamstack/icons/src/icon-components/HeartLiked'
import { trpc } from '@dreamstack/trpc/client'
import {
  useAddFavoriteOptimistic,
  useDeleteFavoriteOptimistic,
} from '../favorites/useFavoritesOptimistic'
import type { ReducedPropertyPreview } from '@dreamstack/web/lib/prisma/properties'
import some from 'lodash/some'
import type { FunctionComponent, MouseEvent } from 'react'
import { useMemo } from 'react'
import { useLocale } from '../../../i18n/src/locale/useLocale'
import { useNotificationPopover } from '../hooks/useNotificationPopover'
import { useMyAccentroBaseUrl } from './routing/useMyAccentroBaseUrl'

const iconWrapperBase = 'cursor-pointer min-w-[32px]'

export const MyLikeButton: FunctionComponent<
  React.PropsWithChildren<{
    property: ReducedPropertyPreview
    propertyId: number
    toggletemporaryFavorites?: (propertyId: number) => void
  }>
> = ({ property, propertyId, toggletemporaryFavorites }) => {
  const locale = useLocale()
  const userId = useUserId()
  const { data, refetch } = trpc.favorites.getMyFavorites.useQuery(
    { locale },
    { enabled: !!userId, trpc: { ssr: false }, staleTime: 0 }
  )

  const { mutateAsync: sendEventFavoriteProp } =
    trpc.tracking.trackEvent.useMutation()

  const liked = useMemo(
    () => some(data, (f) => f.propertyId === propertyId),
    [data, propertyId]
  )

  const handleAuth = useHandleAuthFlowForAction()
  const addFavoriteOptimistic = useAddFavoriteOptimistic()
  const deleteFavorite = useDeleteFavoriteOptimistic()

  const t = useTranslation()

  const myAccentroLink = useMyAccentroBaseUrl()
  const {
    NotificationPopoverComponent,
    openNotification,
    setReferenceElement,
  } = useNotificationPopover({
    title: t('accentro:my.addedFavorite'),
    buttonHref: `/${myAccentroLink}`,
    buttonText: t('accentro:my.myFavoriteList'),
    type: 'success',
    automaticCloseMs: 10000,
    options: { placement: 'left-start' },
    isUnique: true,
    identifierType: 'AddFavorite',
    id: propertyId.toString(),
    offset: [0, 20],
  })

  const onLikeProperty = (event: MouseEvent<Element>) => {
    event.preventDefault()
    if (toggletemporaryFavorites) toggletemporaryFavorites(propertyId)
    const actionCallback = async () => {
      const safeUserId = userId || (await getSession())?.id
      if (!safeUserId) {
        return
      }
      if (liked) {
        await deleteFavorite({ propertyId })
      } else {
        await addFavoriteOptimistic({ propertyId })

        // Open notification popover on like
        openNotification()

        // Cio tracking
        await sendEventFavoriteProp({
          eventName: 'favoriteProperty',
          data: {
            property_id: propertyId,
          },
        })
      }
    }
    handleAuth({
      actionCallback,
    })
  }
  if (!propertyId) return null
  return (
    <div>
      <button
        ref={setReferenceElement as any}
        onClick={(event) => onLikeProperty(event)}
        type="button"
        className={iconWrapperBase}
        aria-pressed={liked}
      >
        {liked ? <HeartLiked /> : <Heart />}
      </button>
      {NotificationPopoverComponent}
    </div>
  )
}
